import type { ProductRecord, ProductImageRecord, ProductVariationRecord } from "@/types/records";
import { useState, useEffect, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, X, Edit2, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Textarea } from "@/components/ui/textarea";

import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Reorder, useDragControls } from "framer-motion";
import { productCoverUrl } from "@/lib/event-cover";
import {
  getEventProductsAll, createEventProduct, deleteEventProduct, updateEventProduct,
  getProductImages, addProductImage, deleteProductImage, uploadProductImage,
  getProductVariations, addProductVariation, updateProductVariation, deleteProductVariation,
} from "@/lib/api-products";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { cn, formatBRL } from "@/lib/utils";
import {
  PanelFieldLabel,
  panelAlert,
  panelBody,
  panelControl,
  panelDialog,
  panelEmpty,
  panelHint,
  panelPage,
  panelPill,
  panelRow,
  panelSurface,
  panelTitle,
} from "@/components/producer/panel";
import { supabase } from "@/integrations/supabase/client";

const EMPTY_ARRAY = [];


interface Props {
  eventId: string;
}

interface ProductRowProps {
  product: ProductRecord;
  onEdit: (product: ProductRecord) => void;
  onDelete: (id: string) => void;
  onToggleVisibility: (id: string, visible: boolean) => void;
  reorderDisabled: boolean;
}

function ProductRow({
  product,
  onEdit,
  onDelete,
  onToggleVisibility,
  reorderDisabled,
}: ProductRowProps) {
  const dragControls = useDragControls();
  const { t } = useTranslation('producerEventForm');

  return (
    <Reorder.Item
      value={product}
      id={product.id}
      dragListener={false}
      dragControls={dragControls}
      className={cn(panelRow, "group relative")}
      whileDrag={{ scale: 1.01, zIndex: 10, boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)" }}
      layout
    >
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button
            type="button"
            aria-label={t('products.row.reorderAriaLabel')}
            onPointerDown={(event) => {
              event.stopPropagation();
              dragControls.start(event);
            }}
            disabled={reorderDisabled}
            className="inline-flex h-10 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground/40 hover:text-foreground hover:bg-muted transition-colors disabled:cursor-not-allowed group-hover:text-muted-foreground"
          >
            <GripVertical className="h-5 w-5" />
          </button>

          <img
            src={productCoverUrl(product)}
            alt={product.name}
            className="h-10 w-10 shrink-0 rounded-xl object-cover"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground truncate">{product.name}</p>
            <p className="text-xs text-muted-foreground">{formatBRL(product.price)}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-4">
          <Switch
            checked={product.is_visible}
            onCheckedChange={(v) => onToggleVisibility(product.id, v)}
          />
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted"
            onClick={() => onEdit(product)}
            title={t('products.actions.edit')}
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            onClick={() => onDelete(product.id)}
            title={t('products.actions.remove')}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Reorder.Item>
  );
}

export function EventProductsManager({ eventId }: Props) {
  const { t } = useTranslation('producerEventForm');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [editingProduct, setEditingProduct] = useState<ProductRecord | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [, setEditorMode] = useState<"create" | "edit">("create");
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);

  const { data: products = EMPTY_ARRAY, isLoading } = useQuery({
    queryKey: ["event-products", eventId],
    queryFn: () => getEventProductsAll(eventId),
  });

  const [localProducts, setLocalProducts] = useState<typeof products>([]);

  useEffect(() => {
    setLocalProducts(products);
  }, [products]);

  const orderedProducts = useMemo(() => {
    return [...localProducts].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [localProducts]);

  const deleteMut = useMutation({
    mutationFn: deleteEventProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event-products", eventId] });
      toast({ title: t('products.toast.productRemoved') });
    },
  });

  const toggleVisibility = useMutation({
    mutationFn: ({ id, visible }: { id: string; visible: boolean }) =>
      updateEventProduct(id, { is_visible: visible }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["event-products", eventId] }),
  });

  const reorderMut = useMutation({
    mutationFn: (updated: ProductRecord[]) =>
      Promise.all(updated.map((p) => updateEventProduct(p.id, { sort_order: p.sort_order }))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event-products", eventId] });
    },
    onError: (e) => toast({ title: t('products.toast.genericErrorTitle'), description: e.message, variant: "destructive" }),
  });

  const handleReorder = (nextProducts: ProductRecord[]) => {
    const updated = nextProducts.map((item, idx) => ({ ...item, sort_order: idx }));
    setLocalProducts(updated);
    reorderMut.mutate(updated);
  };

  const openCreateModal = () => {
    setEditorMode("create");
    setEditingProduct(null);
    setIsEditorOpen(true);
  };

  const openEditModal = (product) => {
    setEditorMode("edit");
    setEditingProduct(product);
    setIsEditorOpen(true);
  };



  return (
    <div className={panelPage}>
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className={panelTitle}>{t('products.card.title')}</h1>
        <Button onClick={openCreateModal} size="sm" className={cn("h-9 px-4", panelPill)}>
          {t('products.card.addButton')}
        </Button>
      </header>

      <div className={panelSurface}>
        <div className={panelBody}>
          <p className={panelHint}>
            <Trans i18nKey="products.notice.text" ns="producerEventForm" components={{ strong: <strong className="font-medium text-foreground" /> }} />
          </p>

          {!isLoading && orderedProducts.length > 0 ? (
            <div className="space-y-2">
              {reorderMut.isPending && (
                <p className={cn(panelHint, "text-right")}>{t('products.savingOrder')}</p>
              )}
              <Reorder.Group axis="y" values={orderedProducts} onReorder={handleReorder} className="space-y-2">
                {orderedProducts.map((p) => (
                  <ProductRow
                    key={p.id}
                    product={p}
                    onEdit={openEditModal}
                    onDelete={setDeletingProductId}
                    onToggleVisibility={(id, visible) => toggleVisibility.mutate({ id, visible })}
                    reorderDisabled={reorderMut.isPending}
                  />
                ))}
              </Reorder.Group>
            </div>
          ) : !isLoading ? (
            <div className={cn(panelEmpty, "flex flex-col items-center justify-center px-6 py-14 text-center")}>
              <p className="text-sm font-medium text-foreground">{t('products.emptyState.title')}</p>
              <p className={cn(panelHint, "max-w-xs")}>{t('products.emptyState.description')}</p>
              <Button onClick={openCreateModal} variant="outline" size="sm" className={cn("mt-4", panelPill)}>
                {t('products.emptyState.createButton')}
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!deletingProductId} onOpenChange={(open) => { if (!open) setDeletingProductId(null); }}>
        <AlertDialogContent className={panelAlert}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-lg font-semibold tracking-tight">{t('products.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('products.deleteDialog.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={panelPill}>{t('products.actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => deletingProductId && deleteMut.mutate(deletingProductId)} className={cn(panelPill, "bg-destructive text-destructive-foreground hover:bg-destructive/90")}>
              {t('products.actions.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ProductEditorModal
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        product={editingProduct}
        userId={user?.id || ""}
        eventId={eventId}
        onSaveSuccess={() => queryClient.invalidateQueries({ queryKey: ["event-products", eventId] })}
      />
    </div>
  );
}

// ─── Thumbnail Image Component with Drag and Drop ───
interface ImageThumbnailProps {
  img: ProductImageRecord;
  isFirst: boolean;
  onDelete: (id: string) => void;
  reorderDisabled: boolean;
}

function ImageThumbnail({
  img,
  isFirst,
  onDelete,
  reorderDisabled,
}: ImageThumbnailProps) {
  const dragControls = useDragControls();
  const { t } = useTranslation('producerEventForm');

  return (
    <Reorder.Item
      value={img}
      id={img.id}
      dragListener={false}
      dragControls={dragControls}
      className="relative shrink-0 w-20 h-20 overflow-hidden rounded-2xl bg-secondary/50 group cursor-pointer"
      whileDrag={{ scale: 1.05, zIndex: 50, boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }}
    >
      <img src={img.image_url} alt={t('products.image.alt')} className="w-full h-full object-cover select-none pointer-events-none" />

      {isFirst && (
        <span className="absolute bottom-0 inset-x-0 bg-primary text-[9px] text-primary-foreground font-semibold text-center py-0.5 select-none uppercase tracking-wider">
          {t('products.image.coverBadge')}
        </span>
      )}

      <div
        className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5"
        onPointerDown={(e) => {
          e.stopPropagation();
          dragControls.start(e);
        }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(img.id);
          }}
          disabled={reorderDisabled}
          className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-full p-1 transition-colors disabled:opacity-50"
          title={t('products.image.removeTitle')}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </Reorder.Item>
  );
}

// ─── Product Editor Modal (Create / Edit) ───

interface ProductEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: ProductRecord | null; // null if create mode
  userId: string;
  eventId: string;
  onSaveSuccess: () => void;
}

function ProductEditorModal({
  isOpen,
  onClose,
  product,
  userId,
  eventId,
  onSaveSuccess,
}: ProductEditorModalProps) {
  const { t } = useTranslation('producerEventForm');
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState(0);
  const [quantityAvailable, setQuantityAvailable] = useState<number | "">("");
  const [maxPerOrder, setMaxPerOrder] = useState<number | "">("");

  // Variations local inputs
  const [varName, setVarName] = useState("");
  const [varValue, setVarValue] = useState("");
  const [editingVar, setEditingVar] = useState<ProductVariationRecord | null>(null);

  // Sync state when product or isOpen changes
  useEffect(() => {
    if (product) {
      setName(product.name || "");
      setDescription(product.description || "");
      setPrice(product.price || 0);
      setQuantityAvailable(product.quantity_available ?? "");
      setMaxPerOrder(product.max_per_order ?? "");
    } else {
      setName("");
      setDescription("");
      setPrice(0);
      setQuantityAvailable("");
      setMaxPerOrder("");
    }
  }, [product, isOpen]);

  // DB queries for edit mode
  const { data: dbImages = EMPTY_ARRAY, refetch: refetchImages } = useQuery({
    queryKey: ["product-images", product?.id],
    queryFn: () => getProductImages(product!.id),
    enabled: !!product?.id && isOpen,
  });

  const { data: dbVariations = EMPTY_ARRAY, refetch: refetchVariations } = useQuery({
    queryKey: ["product-variations", product?.id],
    queryFn: () => getProductVariations(product!.id),
    enabled: !!product?.id && isOpen,
  });


  // Local state for unified management (create vs edit)
  const [localImages, setLocalImages] = useState([]);
  const [localVariations, setLocalVariations] = useState([]);

  useEffect(() => {
    if (product) {
      setLocalImages(dbImages);
    } else {
      setLocalImages([]);
    }
  }, [dbImages, product, isOpen]);

  useEffect(() => {
    if (product) {
      setLocalVariations(dbVariations);
    } else {
      setLocalVariations([]);
    }
  }, [dbVariations, product, isOpen]);

  const uploadMut = useMutation({
    mutationFn: async (files: File[]) => {
      const currentCount = localImages.length;
      const allowed = Math.min(files.length, 10 - currentCount);
      if (allowed <= 0) {
        toast({ title: t('products.toast.photoLimitTitle'), description: t('products.toast.photoLimitDescription'), variant: "destructive" });
        return;
      }
      const uploadedUrls: string[] = [];
      for (let i = 0; i < allowed; i++) {
        const url = await uploadProductImage(files[i], userId);
        uploadedUrls.push(url);
      }

      if (product) {
        // Edit mode: save to DB immediately
        for (let i = 0; i < uploadedUrls.length; i++) {
          await addProductImage(product.id, uploadedUrls[i], currentCount + i);
        }
        // If product doesn't have an image_url, update it to the first uploaded url
        if (!product.image_url && uploadedUrls.length > 0) {
          await updateEventProduct(product.id, { image_url: uploadedUrls[0] });
        }
        refetchImages();
        queryClient.invalidateQueries({ queryKey: ["event-products", eventId] });
      } else {
        // Create mode: add to local state
        setLocalImages((prev) => [
          ...prev,
          ...uploadedUrls.map((url, idx) => ({ id: `temp-${Date.now()}-${idx}-${Math.random()}`, image_url: url }))
        ]);
      }
      toast({ title: t('products.toast.photosAdded', { count: allowed }) });
    },
  });

  const deleteImgMut = useMutation({
    mutationFn: async (id: string) => {
      if (product) {
        await deleteProductImage(id);
        const remaining = localImages.filter((img) => img.id !== id);
        // Also if we deleted the cover image, set the new first image as cover image
        await updateEventProduct(product.id, { image_url: remaining[0]?.image_url || null });
        refetchImages();
        queryClient.invalidateQueries({ queryKey: ["event-products", eventId] });
      } else {
        setLocalImages((prev) => prev.filter((img) => img.id !== id));
      }
    },
    onSuccess: () => {
      toast({ title: t('products.toast.photoRemoved') });
    }
  });

  const addVarMut = useMutation({
    mutationFn: async () => {
      if (!varName.trim() || !varValue.trim()) return;
      if (product) {
        await addProductVariation({
          product_id: product.id,
          name: varName.trim(),
          value: varValue.trim(),
          sort_order: localVariations.length,
        });
        refetchVariations();
      } else {
        setLocalVariations((prev) => [
          ...prev,
          { id: `temp-var-${Date.now()}-${Math.random()}`, name: varName.trim(), value: varValue.trim() }
        ]);
      }
      setVarName(""); setVarValue("");
    },
    onSuccess: () => {
      toast({ title: t('products.toast.variationAdded') });
    }
  });

  const updateVarMut = useMutation({
    mutationFn: async (editing: { id: string; name: string; value: string }) => {
      if (product) {
        await updateProductVariation(editing.id, { name: editing.name, value: editing.value });
        refetchVariations();
      } else {
        setLocalVariations((prev) =>
          prev.map((v) => (v.id === editing.id ? { ...v, name: editing.name, value: editing.value } : v))
        );
      }
      setEditingVar(null);
    },
    onSuccess: () => {
      toast({ title: t('products.toast.variationUpdated') });
    }
  });

  const deleteVarMut = useMutation({
    mutationFn: async (id: string) => {
      if (product) {
        await deleteProductVariation(id);
        refetchVariations();
      } else {
        setLocalVariations((prev) => prev.filter((v) => v.id !== id));
      }
    },
    onSuccess: () => {
      toast({ title: t('products.toast.variationRemoved') });
    }
  });

  const reorderImagesMut = useMutation({
    mutationFn: async (updated: ProductImageRecord[]) => {
      if (product) {
        await Promise.all(
          updated.map((img, idx) =>
            supabase.from("product_images").update({ sort_order: idx }).eq("id", img.id)
          )
        );
        if (updated.length > 0) {
          await updateEventProduct(product.id, { image_url: updated[0].image_url });
        } else {
          await updateEventProduct(product.id, { image_url: null });
        }
        refetchImages();
        queryClient.invalidateQueries({ queryKey: ["event-products", eventId] });
      }
    },
    onError: (e) => toast({ title: t('products.toast.reorderPhotosErrorTitle'), description: e.message, variant: "destructive" }),
  });

  const handleReorderImages = (nextImages: ProductImageRecord[]) => {
    setLocalImages(nextImages);
    if (product) {
      reorderImagesMut.mutate(nextImages);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) uploadMut.mutate(files);
    e.target.value = "";
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSaveProduct = async () => {
    if (!name.trim()) {
      toast({ title: t('products.toast.nameRequiredTitle'), description: t('products.toast.nameRequiredDescription'), variant: "destructive" });
      return;
    }
    setIsSaving(true);
    try {
      if (product) {
        await updateEventProduct(product.id, {
          name: name.trim(),
          description: description.trim() || null,
          price,
          quantity_available: quantityAvailable === "" ? null : Number(quantityAvailable),
          max_per_order: maxPerOrder === "" ? null : Number(maxPerOrder),
        });
        toast({ title: t('products.toast.productUpdated') });
        onSaveSuccess();
        onClose();
      } else {
        // Create mode
        const { data: currentProducts = [] } = await supabase
          .from("event_products")
          .select("id")
          .eq("event_id", eventId);
        
        const sortOrder = currentProducts.length;
        const coverImageUrl = localImages.length > 0 ? localImages[0].image_url : null;

        // 1. Create product
        const newProduct = await createEventProduct({
          event_id: eventId,
          name: name.trim(),
          description: description.trim() || undefined,
          price,
          image_url: coverImageUrl || undefined,
          quantity_available: quantityAvailable === "" ? undefined : Number(quantityAvailable),
          max_per_order: maxPerOrder === "" ? undefined : Number(maxPerOrder),
          sort_order: sortOrder,
        });

        // 2. Add images
        if (localImages.length > 0) {
          await Promise.all(
            localImages.map((img, idx) =>
              addProductImage(newProduct.id, img.image_url, idx)
            )
          );
        }

        // 3. Add variations
        if (localVariations.length > 0) {
          await Promise.all(
            localVariations.map((v, idx) =>
              addProductVariation({
                product_id: newProduct.id,
                name: v.name,
                value: v.value,
                sort_order: idx,
              })
            )
          );
        }

        toast({ title: t('products.toast.productCreated') });
        onSaveSuccess();
        onClose();
      }
    } catch (e) {
      toast({ title: t('products.toast.saveErrorTitle'), description: e.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent mobilePresentation="fullscreen" className={cn(panelDialog, "max-h-[85vh] max-w-2xl overflow-y-auto")}>
        <div className="border-b border-border/60 px-6 py-4">
          <DialogTitle className="font-display text-lg font-semibold tracking-tight">
            {product ? t('products.editor.editTitle') : t('products.editor.createTitle')}
          </DialogTitle>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div className="space-y-5">
            <div>
              <PanelFieldLabel>{t('products.editor.nameLabel')}</PanelFieldLabel>
              <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={150} className={panelControl} />
            </div>

            <div>
              <PanelFieldLabel>{t('products.editor.descriptionLabel')}</PanelFieldLabel>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={500} className="mt-2 rounded-2xl border-transparent bg-secondary/50 focus-visible:ring-1 focus-visible:ring-primary/30" />
            </div>

            <div>
              <PanelFieldLabel>{t('products.editor.priceLabel')}</PanelFieldLabel>
              <Input type="number" min={0} step={0.01} value={price} onChange={(e) => setPrice(parseFloat(e.target.value) || 0)} className={panelControl} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <PanelFieldLabel>{t('products.editor.quantityLabel')}</PanelFieldLabel>
                <Input type="number" min={0} value={quantityAvailable} onChange={(e) => setQuantityAvailable(e.target.value === "" ? "" : Number(e.target.value))} placeholder={t('products.editor.quantityPlaceholder')} className={panelControl} />
              </div>
              <div>
                <PanelFieldLabel>{t('products.editor.maxPerOrderLabel')}</PanelFieldLabel>
                <Input type="number" min={1} value={maxPerOrder} onChange={(e) => setMaxPerOrder(e.target.value === "" ? "" : Number(e.target.value))} placeholder={t('products.editor.quantityPlaceholder')} className={panelControl} />
              </div>
            </div>
          </div>

          <Separator />

          {/* Images */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <PanelFieldLabel>{t('products.editor.photosLabel', { count: localImages.length })}</PanelFieldLabel>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} disabled={localImages.length >= 10 || uploadMut.isPending} />
                <Button variant="outline" size="sm" asChild disabled={localImages.length >= 10}>
                  <span className={cn("gap-1", panelPill)}>{t('products.editor.addPhotoButton')}</span>
                </Button>
              </label>
            </div>
            {uploadMut.isPending && <p className="text-xs text-muted-foreground animate-pulse">{t('products.editor.uploading')}</p>}

            {localImages.length > 0 ? (
              <div className="space-y-1">
                <p className="text-[10px] text-muted-foreground mb-1">
                  {t('products.editor.photosHint')}
                </p>
                <Reorder.Group
                  axis="x"
                  values={localImages}
                  onReorder={handleReorderImages}
                  className="flex gap-2 overflow-x-auto py-2 px-1"
                >
                  {localImages.map((img, idx: number) => (
                    <ImageThumbnail
                      key={img.id}
                      img={img}
                      isFirst={idx === 0}
                      onDelete={(id) => deleteImgMut.mutate(id)}
                      reorderDisabled={reorderImagesMut.isPending}
                    />
                  ))}
                </Reorder.Group>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">{t('products.editor.noPhotos')}</p>
            )}
          </div>

          <Separator />

          {/* Variations */}
          <div className="space-y-2">
            <PanelFieldLabel>{t('products.editor.variationsLabel')}</PanelFieldLabel>

            {localVariations.length > 0 && (
              <div className="space-y-1">
                {localVariations.map((v) => (
                  <div key={v.id} className="flex items-center justify-between rounded-2xl bg-secondary/50 px-3 py-2 text-sm">
                    {editingVar?.id === v.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input value={editingVar.name} onChange={(e) => setEditingVar({ ...editingVar, name: e.target.value })} className="h-7 text-xs" placeholder={t('products.editor.editVariationNamePlaceholder')} />
                        <Input value={editingVar.value} onChange={(e) => setEditingVar({ ...editingVar, value: e.target.value })} className="h-7 text-xs" placeholder={t('products.editor.editVariationValuePlaceholder')} />
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => updateVarMut.mutate(editingVar)}>{t('products.actions.save')}</Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditingVar(null)}>{t('products.actions.cancel')}</Button>
                      </div>
                    ) : (
                      <>
                        <span><strong>{v.name}:</strong> {v.value}</span>
                        <div className="flex gap-1">
                          <button onClick={() => setEditingVar({ id: v.id, name: v.name, value: v.value })} className="text-muted-foreground hover:text-foreground">
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => deleteVarMut.mutate(v.id)} className="text-muted-foreground hover:text-destructive">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Input value={varName} onChange={(e) => setVarName(e.target.value)} placeholder={t('products.editor.newVariationNamePlaceholder')} className={cn("flex-1", panelControl, "mt-0")} maxLength={50} />
              <Input value={varValue} onChange={(e) => setVarValue(e.target.value)} placeholder={t('products.editor.newVariationValuePlaceholder')} className={cn("flex-1", panelControl, "mt-0")} maxLength={200} />
              <Button size="sm" onClick={() => addVarMut.mutate()} disabled={!varName.trim() || !varValue.trim() || addVarMut.isPending} className={cn("h-12 w-12", panelPill)}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">{t('products.editor.variationHint')}</p>
          </div>

          <Separator />

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={isSaving} className={panelPill}>{t('products.actions.cancel')}</Button>
            <Button onClick={handleSaveProduct} disabled={isSaving || !name.trim()} className={panelPill}>
              {product ? t('products.editor.saveButton') : t('products.editor.createButton')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
